import { useState, useEffect } from "react";

import {
    ArrowControls,
    Container,
    Row,
    Column,
    useAnalytics,
    useAnalyticsAttributes,
    useBreakpoints
} from "../../../components";
import { TrelloTemplateModel } from "../../../models";
import { containerWidths } from "../../../styles";

import {
    Controls,
    DraggableContainer,
    ScrollBar,
    ScrollPosition
} from "./FullWidthCarousel.styles";
import TrelloTemplate from "./TrelloTemplate";

type FullWidthCarouselProps = {
    readonly templates: TrelloTemplateModel[];
    transitionTimeInSeconds?: number;
    arrows?: boolean;
    prevArrowLabel?: string;
    nextArrowLabel?: string;
    controlsAlignStart?: boolean;
    readonly reduced?: boolean;
};

const FullWidthCarousel = ({
    templates,
    transitionTimeInSeconds = 0.3,
    arrows,
    prevArrowLabel = "Previous",
    nextArrowLabel = "Next",
    controlsAlignStart = false,
    reduced
}: // eslint-disable-next-line complexity
FullWidthCarouselProps) => {
    const gapSize = 16;

    const [currentIndex, setCurrentIndex] = useState<number | undefined>(0);
    const [preventScroll, setPreventScroll] = useState(false);
    const [windowWidth, setWindowWidth] = useState(0);
    const [marginLeft, setMarginLeft] = useState(0 - gapSize);
    const [linksDisabled, setLinksDisabled] = useState(false);
    const [drag, setDrag] = useState<{
        dragStart: [number, number];
        startingMargin: number;
        lastIndex: number;
    } | null>(null);

    const breakpoints = useBreakpoints();

    //number of visible slides
    const show: number = breakpoints.includes("lg") ? 3 : 1;

    const slideWidth = breakpoints.includes("xl")
        ? containerWidths.xl / 3
        : breakpoints.includes("lg")
        ? containerWidths.lg / 3
        : breakpoints.includes("md")
        ? (containerWidths.md * 5) / 6
        : breakpoints.includes("sm")
        ? (containerWidths.sm * 5) / 6
        : (windowWidth * 5) / 6;

    const atBeginning =
        typeof currentIndex !== "undefined" && currentIndex === 0;
    const atEnd =
        typeof currentIndex !== "undefined" &&
        currentIndex >= templates.length - show;

    //analytics
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    //watch window width
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    //move carousel (adjust marginLeft) when selectedIndex changes
    useEffect(() => {
        if (typeof currentIndex === "undefined") return;
        const newMargin = 0 - gapSize - currentIndex * slideWidth;
        setMarginLeft(newMargin);
    }, [currentIndex, breakpoints, windowWidth, slideWidth, templates.length]);

    useEffect(() => {
        if (preventScroll) {
            document.body.classList.add("prevent-scroll");
            document.ontouchmove = (e) => {
                e.preventDefault();
            };
        } else {
            document.body.classList.remove("prevent-scroll");
            document.ontouchmove = () => true;
        }
        return () => {
            document.body.classList.remove("prevent-scroll");
            document.ontouchmove = () => true;
        };
    }, [preventScroll]);

    //listen to slide updates
    useEffect(() => {
        if (typeof currentIndex === "undefined") return;
        sendTrackEvent("carouselChanged", {
            ...analyticsAttributes,
            eventComponent: "carousel",
            event: "changed",
            label: `Slide ${currentIndex + 1}`,
            labelEnglish: `Slide ${currentIndex + 1}`
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: add missing dependencies
    }, [currentIndex]);

    const next = () => {
        if (typeof currentIndex === "undefined") return;
        if (atEnd) {
            setMarginLeft(marginLeft - slideWidth / 5);
            setTimeout(() => {
                setMarginLeft(marginLeft);
            }, 125);
        }
        const newIndex = Math.min(currentIndex + show, templates.length - show);
        setCurrentIndex(newIndex);
    };

    const prev = () => {
        if (typeof currentIndex === "undefined") return;
        if (atBeginning) {
            setMarginLeft(marginLeft + slideWidth / 5);
            setTimeout(() => {
                setMarginLeft(marginLeft);
            }, 125);
        }
        const newIndex = Math.max(currentIndex - show, 0);
        setCurrentIndex(newIndex);
    };

    const dragStart = (x: number, y: number) => {
        if (typeof currentIndex === "undefined") return;
        setDrag({
            dragStart: [x, y],
            startingMargin: marginLeft,
            lastIndex: currentIndex
        });
        setLinksDisabled(true);
        setCurrentIndex(undefined);
    };

    const dragging = (x: number, y: number) => {
        //check if drag started on carousel
        if (!drag) return;

        //exit if dragging vertically
        const horizontalOffset = x - drag.dragStart[0];
        const verticalOffset = y - drag.dragStart[1];
        if (Math.abs(horizontalOffset) < Math.abs(verticalOffset)) return;

        //horizontal touch, lock vertical scroll
        if (!preventScroll) setPreventScroll(true);

        //move track
        const dragAmount = horizontalOffset + drag.startingMargin;
        const maxAmount = 0;
        const minAmount =
            0 -
            slideWidth * (templates.length - show) -
            (templates.length - 1) * gapSize;
        const friction = 3;

        const amountWithFriction =
            dragAmount > maxAmount
                ? dragAmount / friction
                : dragAmount < minAmount
                ? minAmount + (dragAmount - minAmount) / friction
                : dragAmount;

        setMarginLeft(amountWithFriction);
    };

    const dragEnd = (x: number) => {
        if (!drag) return;
        const dragAmount = drag.dragStart[0] - x;
        const newIndex = Math.round(dragAmount / slideWidth) + drag.lastIndex;
        const max = templates.length - show;
        const useIndex = Math.max(0, Math.min(max, newIndex));
        if (Math.abs(dragAmount) > 20) {
            //looks like a drag, keep links disabled for a beat
            setTimeout(() => setLinksDisabled(false), 100);
        } else {
            //looks like a click, disable links right away
            setLinksDisabled(false);
        }
        setPreventScroll(false);
        setCurrentIndex(useIndex);
        setDrag(null);
    };

    return (
        <>
            <Container>
                <Controls alignStart={controlsAlignStart}>
                    {arrows && (
                        <ArrowControls
                            next={next}
                            nextAriaDisabled={atEnd}
                            nextLabel={nextArrowLabel}
                            prevAriaDisabled={atBeginning}
                            prev={prev}
                            prevLabel={prevArrowLabel}
                        />
                    )}
                </Controls>
            </Container>
            <DraggableContainer
                onTouchStart={({ touches }) => {
                    if (touches[0]) {
                        dragStart(touches[0].clientX, touches[0].clientY);
                    }
                }}
                onTouchMove={({ touches }) => {
                    if (touches[0]) {
                        dragging(touches[0].clientX, touches[0].clientY);
                    }
                }}
                onTouchEnd={({ changedTouches }) => {
                    if (changedTouches[0]) {
                        dragEnd(changedTouches[0].clientX);
                    }
                }}
                onMouseDown={(e) => dragStart(e.clientX, e.clientY)}
                onMouseMove={(e) => dragging(e.clientX, e.clientY)}
                onMouseUp={(e) => dragEnd(e.clientX)}
                onMouseLeave={(e) => dragEnd(e.clientX)}
            >
                <Container>
                    <Row
                        style={{
                            marginLeft,
                            cursor: drag ? "grabbing" : "grab",
                            transition: drag
                                ? "none"
                                : `margin-left ${transitionTimeInSeconds}s ease`
                        }}
                    >
                        {templates
                            .filter(({ type }) => type === "trelloTemplate")
                            .map((template, index) => (
                                <Column
                                    xs={{ span: "10" }}
                                    lg={{ span: "4" }}
                                    key={index}
                                >
                                    <TrelloTemplate
                                        model={template}
                                        reduced={reduced}
                                        disabled={linksDisabled}
                                    />
                                </Column>
                            ))}
                    </Row>
                </Container>
            </DraggableContainer>
            <Container>
                <ScrollBar>
                    <ScrollPosition
                        transitionTimeInSeconds={transitionTimeInSeconds}
                        style={{
                            width: `${(100 / templates.length) * show}%`,
                            left: `${
                                ((currentIndex ?? drag?.lastIndex ?? 0) /
                                    templates.length) *
                                100
                            }%`
                        }}
                    />
                </ScrollBar>
            </Container>
        </>
    );
};

export default FullWidthCarousel;
